import React from "react";
import PropTypes from "prop-types";
import { Button, Pagination } from "semantic-ui-react";
import FeedbackCard from "./FeedbackCard";

const CategoryColumn = ({
  category,
  feedbacks,
  currentPage,
  pageSize,
  onPageChange,
  onEdit,
  onDelete,
  onMarkAsRead,
  onMarkAllAsRead,
  onOpenModal,
  getUserInfo,
  markingAsRead,
  markingAllAsRead,
  categoryConfig,
  isLoading,
}) => {
  const { isAllowedToEdit } = getUserInfo();
  const totalCount = feedbacks.length;
  const unreadCount = feedbacks.filter((fb) => !fb.is_read).length;
  const categoryColor = categoryConfig[category].color;
  const isMarkingAll = markingAllAsRead[category] || false;

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedFeedbacks = feedbacks.slice(startIndex, startIndex + pageSize);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const canDeleteFeedback = (feedback) => {
    const { isAllowedToEdit, userName } = getUserInfo();
    if (isAllowedToEdit) return true;
    if (feedback.created_by && feedback.created_by === userName) return true;
    return false;
  };

  const canEditFeedback = (feedback) => {
    const { userName } = getUserInfo();
    return feedback.created_by && feedback.created_by === userName;
  };

  return (
    <div
      style={{
        flex: 1,
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        padding: "1rem",
        margin: "2.5rem 1.5rem 0",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
          padding: "0.75rem",
          backgroundColor: "#fff",
          borderRadius: "6px",
          border: "1px solid #e0e0e0",
          fontWeight: "bold",
          fontSize: "1rem",
        }}
      >
        <span>{category}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {isAllowedToEdit && unreadCount > 0 && (
            <Button
              size="mini"
              color="green"
              loading={isMarkingAll}
              disabled={isMarkingAll}
              onClick={() => onMarkAllAsRead(category)}
              style={{
                fontSize: "0.7rem",
                padding: "0.5rem 0.6rem",
              }}
            >
              {isMarkingAll ? "Marking..." : `Mark All (${unreadCount})`}
            </Button>
          )}
          {!isAllowedToEdit && (
            <button
              onClick={() => onOpenModal(category)}
              style={{
                backgroundColor: categoryColor,
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                cursor: "pointer",
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              +
            </button>
          )}
          {isAllowedToEdit && (
            <span
              style={{
                backgroundColor: categoryColor,
                color: "white",
                borderRadius: "12px",
                padding: "0.2rem 0.5rem",
                fontSize: "0.8rem",
                fontWeight: "bold",
              }}
            >
              {totalCount}
            </span>
          )}
        </div>
      </div>
      {paginatedFeedbacks.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            color: "#666",
            padding: "2rem",
            fontSize: "0.9rem",
          }}
        >
          No {category.toLowerCase()} yet.
          <br />
          {!isAllowedToEdit && "Be the first to contribute!"}
        </div>
      ) : (
        paginatedFeedbacks.map((fb) => (
          <FeedbackCard
            key={fb.id || fb._id}
            feedback={fb}
            onEdit={onEdit}
            onDelete={onDelete}
            onMarkAsRead={onMarkAsRead}
            canEdit={canEditFeedback(fb)}
            canDelete={canDeleteFeedback(fb)}
            isAllowedToEdit={isAllowedToEdit}
            isBeingMarkedAsRead={markingAsRead[fb.id || fb._id] || false}
          />
        ))
      )}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "1rem",
          }}
        >
          <Pagination
            totalPages={totalPages}
            activePage={currentPage}
            onPageChange={(e, data) =>
              onPageChange(category, Number(data.activePage))
            }
            disabled={isLoading}
            size="mini"
          />
        </div>
      )}
    </div>
  );
};

CategoryColumn.propTypes = {
  category: PropTypes.string.isRequired,
  feedbacks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      _id: PropTypes.string,
      message: PropTypes.string.isRequired,
      is_read: PropTypes.bool,
    })
  ).isRequired,
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMarkAsRead: PropTypes.func.isRequired,
  onMarkAllAsRead: PropTypes.func.isRequired,
  onOpenModal: PropTypes.func.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  markingAsRead: PropTypes.objectOf(PropTypes.bool).isRequired,
  markingAllAsRead: PropTypes.objectOf(PropTypes.bool).isRequired,
  categoryConfig: PropTypes.objectOf(
    PropTypes.shape({
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default CategoryColumn;
